import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Tooltip, Typography, styled, useTheme } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetCustomPropertiesByIds } from 'src/api/custom-property'
import { useGetMainComponentCustomProperties } from 'src/api/main-component-type'
import { Months } from 'src/enums'
import { ILocality } from 'src/generated-types'
import { ICustomProperty } from 'src/generated-types/customProperty'
import { IMainComponent } from 'src/generated-types/main-component'
import { IMainComponentProperty } from 'src/generated-types/main-component-property'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import {
  findCustomPropertyByKey,
  getCustomPropertyValueWithKey,
  getUnresolvedCustomPropertyIds,
  normalizeCustomPropertyValue,
  parseCustomPropertyValues,
  resolveCustomPropertyKeyLabel,
  resolveCustomPropertyLabel,
} from 'src/utils/customPropertyValues'
import { getCageName } from 'src/utils/function'
import EquipmentSectionLayout from './EquipmentSectionLayout'
import i18n from 'src/i18n'

interface Props {
  equipment: IMainComponent
  locality?: ILocality
  customerId: EntityId
}

type FieldValue = number | string | null | undefined

const isEmpty = (value: FieldValue): boolean => value == null || (typeof value === 'string' && value.trim() === '')

interface FieldProps {
  label: string
  value: FieldValue
  unit?: string
  fullWidth?: boolean
  numeric?: boolean
}

const Field = ({ label, value, unit, fullWidth = false, numeric = false }: FieldProps) => {
  const valueRef = useRef<HTMLElement>(null)
  const [isClipped, setIsClipped] = useState(false)
  const valueText = `${value}${unit ? ` ${unit}` : ''}`

  useEffect(() => {
    const element = valueRef.current
    if (element) setIsClipped(element.scrollWidth > element.offsetWidth)
  }, [valueText, fullWidth])

  return (
    <Box sx={{ gridColumn: fullWidth ? '1 / -1' : 'auto', minWidth: 0 }}>
      <StyledFieldLabel>{label}</StyledFieldLabel>
      <Tooltip title={valueText} disableHoverListener={!isClipped}>
        <StyledFieldValue ref={valueRef} fullWidth={fullWidth} numeric={numeric}>
          {valueText}
        </StyledFieldValue>
      </Tooltip>
    </Box>
  )
}

// Skip-empty factory: returns null for empty values so groups can detect
// whether they have any populated fields and the grid only gets real cells.
const buildField = (key: string, props: FieldProps): JSX.Element | null => {
  if (isEmpty(props.value)) return null
  return <Field key={key} {...props} />
}

const Group = ({ title, children }: { title: string; children: Array<JSX.Element | null> }) => {
  const theme = useTheme()
  const populated = children.some(Boolean)
  if (!populated) return null
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          color: getThemeColor(theme, MUIThemeColors.primaryMain),
          mb: '6px',
          display: 'block',
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          columnGap: '16px',
          rowGap: '7px',
          alignItems: 'start',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

const resolveCustomLabel = (property: ICustomProperty): string => {
  const raw = property.label
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return (i18n.language === 'en' ? parsed?.en : parsed?.no) ?? parsed?.no ?? parsed?.en ?? ''
  } catch {
    return typeof raw === 'string' ? raw : ''
  }
}

const buildCustomPropertyFields = (
  customProperties: ICustomProperty[] | undefined,
  customValues: Record<string, unknown>,
  labelLookupProperties: ICustomProperty[] | undefined,
  customPropertiesLoading: boolean,
  unresolvedPropertiesLoading: boolean,
): Array<JSX.Element | null> => {
  if (customPropertiesLoading) return []

  const consumedKeys = new Set<string>()
  const fields: Array<JSX.Element | null> = []

  for (const property of customProperties ?? []) {
    const { value, matchedKey } = getCustomPropertyValueWithKey(customValues, property)
    if (matchedKey) consumedKeys.add(matchedKey)
    fields.push(
      buildField(`custom-${property.id}`, {
        label: resolveCustomPropertyLabel(property),
        value,
      }),
    )
  }

  Object.entries(customValues).forEach(([key, rawValue]) => {
    if (consumedKeys.has(key)) return

    const isUnresolvedId = /^\d+$/.test(key) && !findCustomPropertyByKey(key, customProperties)
    if (isUnresolvedId && unresolvedPropertiesLoading) return

    fields.push(
      buildField(`custom-orphan-${key}`, {
        label: resolveCustomPropertyKeyLabel(key, labelLookupProperties),
        value: normalizeCustomPropertyValue(rawValue),
      }),
    )
  })

  return fields
}

export const EquipmentMetadataSection = ({ equipment, locality, customerId }: Props) => {
  const { t } = useTranslation([
    'main-component',
    'common',
    'maintenance',
    'main-component-property',
    'interactive-schema',
  ])
  const theme = useTheme()

  const { data: customProperties, isLoading: customPropertiesLoading } = useGetMainComponentCustomProperties(
    equipment.typeId as EntityId,
    customerId,
  )
  const customValues = useMemo(
    () => parseCustomPropertyValues(equipment.customPropertyValues),
    [equipment.customPropertyValues],
  )

  const unresolvedPropertyIds = useMemo(
    () => getUnresolvedCustomPropertyIds(customValues, customProperties),
    [customValues, customProperties],
  )

  const { data: unresolvedCustomProperties = [], isLoading: unresolvedPropertiesLoading } =
    useGetCustomPropertiesByIds(customerId, unresolvedPropertyIds)

  const labelLookupProperties = useMemo(() => {
    const byId = new Map<number, ICustomProperty>()
    for (const property of [...(customProperties ?? []), ...unresolvedCustomProperties]) {
      byId.set(property.id, property)
    }
    return Array.from(byId.values())
  }, [customProperties, unresolvedCustomProperties])

  // Maintenance templates carry { id, name } at runtime even though the
  // generated type only exposes mainComponentId/mainComponent.
  const maintenanceTemplateLabel = useMemo<string | null>(() => {
    const templates = (equipment.mainComponentMaintenanceTemplates ?? []) as Array<{ name?: string }>
    const names = templates.map(x => x?.name).filter((n): n is string => Boolean(n && n.trim()))
    if (names.length > 0) return names.join(', ')
    return templates.length > 0 ? String(templates.length) : null
  }, [equipment.mainComponentMaintenanceTemplates])

  const productionMonthLabel = useMemo<string | null>(() => {
    const m = equipment.productionMonth
    if (m == null) return null
    return Months[m] ?? null
  }, [equipment.productionMonth])

  const properties = (equipment.mainComponentProperties ?? []) as IMainComponentProperty[]
  const hasMultipleProperties = properties.length > 1

  // Placement chain (reuse the logic from EquipmentEnvironmentSection).
  const placementChain = useMemo<string | null>(() => {
    const localityName = locality?.name ?? null
    const mooringName = equipment.position?.mooringName ?? null
    const positionName = equipment.position ? getCageName(equipment.position.name, equipment.position.reference) : null
    const chain = [localityName, mooringName, positionName].filter(Boolean).join(' → ')
    return chain || null
  }, [locality?.name, equipment.position])

  const depth = equipment.position?.depth ?? null

  const placementValue = useMemo<string | null>(() => {
    if (equipment.assignedServiceStationName) {
      return equipment.assignedServiceStationName || placementChain
    }
    if (equipment.storageName) {
      return equipment.storageName || placementChain
    }
    return placementChain
  }, [
    equipment.componentServiceStations,
    equipment.storedEquipment,
    equipment.assignedServiceStationName,
    equipment.storageName,
    placementChain,
  ])

  const propertyFields = properties.flatMap((p, index) => {
    const suffix = hasMultipleProperties ? ` #${index + 1}` : ''
    return [
      buildField(`hs-${index}`, {
        label: `${t('hs', { ns: 'main-component-property' })}${suffix}`,
        value: p.hs ?? null,
        numeric: true,
      }),
      buildField(`vc5-${index}`, { label: `VC₅${suffix}`, value: p.vC5 ?? null, numeric: true }),
      buildField(`vc15-${index}`, { label: `VC₁₅${suffix}`, value: p.vC15 ?? null, numeric: true }),
    ]
  })

  const customPropertyFields = buildCustomPropertyFields(
    customProperties,
    customValues,
    labelLookupProperties,
    customPropertiesLoading,
    unresolvedPropertiesLoading,
  )

  const generalFields: Array<JSX.Element | null> = [
    buildField('description', {
      label: t('description', { ns: 'common' }),
      value: equipment.description,
      fullWidth: true,
    }),
    buildField('serialNumber', { label: t('serialNumber', { ns: 'main-component' }), value: equipment.serialNumber }),
    buildField('orderNumber', { label: t('orderNumber', { ns: 'main-component' }), value: equipment.orderNumber }),
    buildField('reference', {
      label: t('reference', { ns: 'main-component' }),
      value: (equipment as { reference?: string }).reference ?? equipment.position?.reference ?? null,
    }),
    buildField('productionYear', {
      label: t('productionYear', { ns: 'main-component' }),
      value: equipment.productionYear,
      numeric: true,
    }),
    buildField('productionMonth', {
      label: t('productionMonth', { ns: 'main-component' }),
      value: productionMonthLabel,
    }),
    buildField('supplier', { label: t('supplier', { ns: 'common' }), value: equipment.supplier?.name ?? null }),
    buildField('maintenanceTemplate', {
      label: t('maintenance-template', { ns: 'maintenance' }),
      value: maintenanceTemplateLabel,
    }),
    buildField('lifetime', {
      label: t('lifetime', { ns: 'main-component' }),
      value: equipment.lifetime,
      numeric: true,
    }),
    ...customPropertyFields,
  ]

  const propertyGroupFields: Array<JSX.Element | null> = [
    buildField('solidity', {
      label: t('solidity', { ns: 'main-component' }),
      value: equipment.solidity,
      numeric: true,
    }),
    buildField('circumference', {
      label: t('circumference', { ns: 'main-component' }),
      value: equipment.circumference,
      numeric: true,
    }),
    ...propertyFields,
  ]

  const isStorageOrServiceStation = Boolean(equipment.assignedServiceStationName || equipment.storageName)

  const placementFields: Array<JSX.Element | null> = [
    buildField('placement', { label: t('placement', { ns: 'common' }), value: placementValue, fullWidth: true }),
    ...(!isStorageOrServiceStation
      ? [
        buildField('depth', { label: t('depth', { ns: 'common' }), value: depth, unit: 'm', numeric: true }),
        buildField('waveHeight', { label: 'Bølgehøyde (Hs)', value: locality?.hs ?? null, unit: 'm', numeric: true }),
        buildField('current5', { label: 'Strøm 5 m', value: locality?.vC5M ?? null, unit: 'm/s', numeric: true }),
        buildField('current15', { label: 'Strøm 15 m', value: locality?.vC15M ?? null, unit: 'm/s', numeric: true }),
      ]
      : []),
  ]

  const hasAnyField = [...generalFields, ...propertyGroupFields, ...placementFields].some(Boolean)
  if (!hasAnyField) return null

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', pt: '4px' }}>
      <Group title={t('step-general', { ns: 'interactive-schema' })}>{generalFields}</Group>
      <Group title={t('main-component-property', { ns: 'main-component' })}>{propertyGroupFields}</Group>
      <Group title={t('placement', { ns: 'common' })}>{placementFields}</Group>
    </Box>
  )

  return (
    <EquipmentSectionLayout
      title={t('details', { ns: 'common' })}
      icon={<InfoOutlinedIcon sx={{ fontSize: '16px', color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
      content={content}
    />
  )
}

const StyledFieldLabel = styled(Typography)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
  fontSize: '11px',
  lineHeight: 1.3,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  display: 'block',
}))

const StyledFieldValue = styled(Typography, {
  shouldForwardProp: prop => prop !== 'fullWidth' && prop !== 'numeric',
})<{ fullWidth?: boolean; numeric?: boolean }>(({ theme, fullWidth, numeric }) => ({
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.35,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
  wordBreak: fullWidth ? 'break-word' : 'normal',
  whiteSpace: fullWidth ? 'normal' : 'nowrap',
  overflow: fullWidth ? 'visible' : 'hidden',
  textOverflow: fullWidth ? 'clip' : 'ellipsis',
  fontVariantNumeric: numeric ? 'tabular-nums' : 'normal',
}))

export default EquipmentMetadataSection
